import { LoadingButton } from '@mui/lab'
import { Paper, Typography } from '@mui/material'
import { SnackbarProvider, enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import DashboardErrorView from '../components/DashboardErrorView'
import DashboardLayout from '../components/DashboardLayout'
import Divider from '@mui/material/Divider'
import SelectOrgs from '@components/SelectOrgs'
import SelectUsers, { UserDropDownOption } from '@components/SelectUsers'
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation'
import API from '@enums/api'
import { describeError } from '@utils/errors'

const TransferUser = () => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<boolean>(false)
    const [resetting, setResetting] = useState<boolean>(false)

    const [selectedOldOrg, setSelectedOldOrg] = useState<string | undefined>(
        undefined
    )
    const [selectedUser, setSelectedUser] = useState<string | undefined>(
        undefined
    )
    const [selectedNewOrg, setSelectedNewOrg] = useState<string | undefined>(
        undefined
    )

    const [showSelectUser, setShowSelectUser] = useState(false)
    const [showSelectNewOrg, setShowSelectNewOrg] = useState(false)
    const [showSubmit, setShowSubmit] = useState(false)

    // const [orgOptions, setOrgOptions] = useState<OrgDropDownOption[]>([])
    const [userOptions, setUserOptions] = useState<UserDropDownOption[]>([])

    useEffect(() => {
        setLoading(true)
        setShowSelectUser(false)
        setTimeout(() => {
            setShowSelectUser(true)
            setLoading(false)
        }, 500)
    }, [selectedOldOrg])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!selectedNewOrg || !selectedOldOrg || !selectedUser) return

        setLoading(true)

        if (selectedOldOrg === selectedNewOrg) {
            enqueueSnackbar('New org cannot be the same as the old org.', {
                variant: 'error',
            })
            setError('New org cannot be the same as the old org.')
            setTimeout(() => {
                setError(null)
            }, 2000)
            setLoading(false)
            return
        }

        // call the endpoint to transfer a user
        const res = await API.actions.transferUser(
            selectedOldOrg,
            selectedNewOrg,
            selectedUser
        )

        if (!res.ok) {
            setError(describeError(res, 'Failed to transfer user'))
            setLoading(false)
            return
        }

        setSuccess(true)
        setResetting(true)
        setTimeout(() => {
            resetForm()
            setLoading(false)
        }, 2000)
    }

    const resetForm = () => {
        setSelectedOldOrg(undefined)
        setSelectedNewOrg(undefined)
        setSelectedUser(undefined)
        setShowSelectUser(false)
        setShowSelectNewOrg(false)
        setShowSubmit(false)
        setResetting(false)
    }

    if (loading) {
        enqueueSnackbar('Loading...', { variant: 'info' })
    }

    if (resetting) {
        return (
            <DashboardLayout loading>
                <SnackbarProvider
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                />
            </DashboardLayout>
        )
    }

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    if (success) {
        enqueueSnackbar('User transferred successfully', { variant: 'success' })
        setSuccess(false)
    }

    return (
        <DashboardLayout>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 64,
                }}
            >
                <Paper
                    style={{
                        paddingInline: 32,
                        paddingBlock: 32,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            marginBottom: 2,
                        }}
                    >
                        Select an organisation you want to transfer a user from
                    </Typography>
                    <SelectOrgs
                        selectedOrg={selectedOldOrg}
                        setSelectedOrg={setSelectedOldOrg}
                        setShowForm={setShowSelectUser}
                    />
                    {showSelectUser && selectedOldOrg !== undefined && (
                        <div
                            style={{
                                display: 'flex',
                                marginTop: 32,
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 16,
                            }}
                        >
                            <Divider variant="middle" />
                            <Typography
                                variant="body1"
                                sx={{
                                    marginBottom: 2,
                                }}
                            >
                                Select a user to transfer
                            </Typography>
                            <SelectUsers
                                options={userOptions}
                                setOptions={setUserOptions}
                                selectedUser={selectedUser}
                                setSelectedUser={setSelectedUser}
                                setShowForm={setShowSelectNewOrg}
                                selectedOrg={selectedOldOrg}
                            />
                        </div>
                    )}
                    {showSelectNewOrg &&
                        selectedOldOrg !== undefined &&
                        selectedUser !== undefined && (
                            <div
                                style={{
                                    display: 'flex',
                                    marginTop: 32,
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 16,
                                }}
                            >
                                <Divider variant="middle" />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        marginBottom: 2,
                                    }}
                                >
                                    Select organization to transfer user to
                                </Typography>
                                <SelectOrgs
                                    // options={orgOptions}
                                    // setOptions={setOrgOptions}
                                    selectedOrg={selectedNewOrg}
                                    setSelectedOrg={setSelectedNewOrg}
                                    setShowForm={setShowSubmit}
                                />
                            </div>
                        )}
                    {showSubmit && (
                        <LoadingButton
                            style={{ marginTop: 32 }}
                            startIcon={<TransferWithinAStationIcon />}
                            size="small"
                            variant="contained"
                            onClick={handleSubmit}
                        >
                            Transfer
                        </LoadingButton>
                    )}
                </Paper>
            </div>
            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}

export default TransferUser
